import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AlertService } from '@app/_services';

import { AuthenticationService } from '@app/_services';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
	private isRedirectingToLogin = false;
	constructor(
		private authenticationService: AuthenticationService,
		private router: Router,
		private alertService: AlertService) {
		// Reset redirect flag when a user logs in again
		try {
			this.authenticationService.currentUser?.subscribe(user => {
				if (user) {
					this.isRedirectingToLogin = false;
				}
			});
		} catch (e) { }
	}

	// Endpoints whose 401 should NOT trigger a global logout.
	// A 401 from these paths means the request was rejected (e.g. role/permission issue)
	// but the user's session token is still valid — we should show an error, not log out.
	// The pattern /\/pictures([/?#]|$)/ matches:
	//   /pictures          (exact, end of string)
	//   /pictures/         (followed by slash — e.g. /pictures/123)
	//   /pictures?...      (followed by query string — e.g. GET /pictures?size=200)
	//   /pictures#...      (followed by fragment)
	private readonly NON_AUTH_401_PATTERNS = [
		/\/pictures([/?#]|$)/,
	];

	private isMediaEndpoint(url: string): boolean {
		return this.NON_AUTH_401_PATTERNS.some(pattern => pattern.test(url));
	}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(catchError(err => {
			if (err.status === 401) {
				// auto logout only when request actually had auth header
				// (prevents unrelated 401s from wiping current user/session)
				const hadAuthorizationHeader = request.headers.has('Authorization');
				if (!hadAuthorizationHeader) {
					return throwError(err);
				}
				// Do NOT log out for media/picture upload endpoints — a 401 there means
				// the upload was rejected but the user's session is still valid.
				if (this.isMediaEndpoint(request.url)) {
					return throwError(err);
				}
				// auto logout if 401 response returned from authenticated api request
				const currentUrl = this.router.url;
				// Extract the session-expiry message from the backend response so the
				// login page can show "Session expired — please login." instead of a
				// silent redirect.  Backend writes this message in JwtAuthenticationFilter
				// when it catches ExpiredJwtException (fix cc5c5b7fe).
				const sessionMsg: string = err?.error?.message || '';
				this.authenticationService.logout();
				if (!this.isRedirectingToLogin && !currentUrl.startsWith('/login')) {
					this.isRedirectingToLogin = true;
					const queryParams: any = { returnUrl: currentUrl };
					if (sessionMsg) {
						queryParams['sessionMsg'] = sessionMsg;
					}
					this.router.navigate(['login'], { queryParams }).then(() => {
						// no-op; flag resets on next successful login
					});
				}
				// Swallow the error to prevent multiple popups across pending requests
				return of(null);
			}

			// 504 Gateway Timeout on a mutating request (POST/PATCH/PUT/DELETE):
			// Apache proxy timed out, but the backend may have already completed the write.
			// Do NOT silently allow a retry — surface a clear warning so the user checks
			// before resubmitting.  The thrown error message propagates to component error
			// callbacks, which keep their formLoading/isSubmitting flag set to prevent
			// in-component duplicate submissions.
			const isMutatingMethod = ['POST', 'PATCH', 'PUT', 'DELETE'].includes(request.method);
			if (err.status === 504 && isMutatingMethod) {
				const gatewayTimeoutMsg = 'The server took too long to respond. Your action MAY have been saved — ' +
					'please refresh the page and check before trying again. Do NOT click submit again without checking first.';
				console.warn('ERROR INTERCEPTOR: 504 on ' + request.method + ' ' + request.url + ' — possible duplicate write risk');
				return throwError(gatewayTimeoutMsg);
			}

			let errorMsg = 'General Error.';

			if (JSON.stringify(err.error) === '{"isTrusted":true}') {
				errorMsg = 'The server is not responding. Please try again in a moment. If this continues, contact support.';
			}
			else {
				console.log('ERROR INTERCEPTOR: err is: ',err,err?.errors);

				errorMsg = err?.error?.errors?.length > 0 ? err.error : err?.error?.message || err?.error?.error || err?.message || err?.statusText || 'An unexpected server error occurred. Please try again.';
				console.log('ERROR INTERCEPTOR: FINAL message is: ' + JSON.stringify(errorMsg));
			}
			return throwError(errorMsg);
		}));
	}
}
